import * as React from 'react';
import { Search, TriangleAlert, Plus, type LucideIcon } from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  Input,
  Button,
} from '../primitives';
import { cn } from '../../lib/utils';
import { EmptyState } from '../EmptyState';
import type { ListViewResult } from '../../hooks/useListView';

export interface DataListColumn<TNode> {
  header: React.ReactNode;
  cell: (_node: TNode) => React.ReactNode;
  align?: 'left' | 'right';
  /** Optional className for the header + cell (e.g. width). */
  className?: string;
}

export interface DataListProps<TNode, TFilters> {
  list: ListViewResult<TNode, TFilters>;
  columns: DataListColumn<TNode>[];
  getRowId: (_node: TNode) => string;
  onRowClick?: (_node: TNode) => void;
  searchPlaceholder?: string;
  /** A filter control (e.g. the filter builder) rendered beside the search box. */
  filterSlot?: React.ReactNode;
  /** Active-filter chips rendered under the toolbar. */
  selectedFilters?: React.ReactNode;
  /**
   * How the list is ordered, stated at the right of the toolbar. Every list has
   * an order; saying it out loud stops "why is this one first?".
   */
  sortLabel?: React.ReactNode;
  /**
   * How many rows the current filters produced. Stated in the sort label's
   * place while any filter is on: with filters applied the useful fact is what
   * they did, and the ordering is still true but no longer the question. Unset
   * (no filters) the sort label stands.
   */
  matchCount?: number;
  emptyTitle: string;
  emptyDescription?: string;
  /**
   * `open` drops the card wrapper so the rows sit directly on the page — the
   * treatment for a results list, where a card adds a border around content
   * that is already the whole point of the screen. `card` keeps the panel, for
   * lists embedded inside a page that has other content.
   */
  variant?: 'card' | 'open';
  /** Rendered in the empty state — e.g. the active filters, so they can be cleared there. */
  emptyChildren?: React.ReactNode;
  /** Primary action offered when the list is empty. */
  emptyAction?: { label: string; onAction: () => void };
  /** Quieter follow-up under the empty state's action — usually an explainer link. */
  emptySecondary?: React.ReactNode;
  /** Overrides the icon chosen from whether the list is being narrowed. */
  emptyIcon?: LucideIcon;
  /** Set when filters are applied, so the empty state reads as "no results". */
  emptyNarrowed?: boolean;
  testId?: string;
}

export function DataList<TNode, TFilters>({
  list,
  columns,
  getRowId,
  onRowClick,
  searchPlaceholder = 'Search',
  filterSlot,
  selectedFilters,
  sortLabel,
  matchCount,
  emptyTitle,
  emptyDescription = 'Modify your search criteria and try again.',
  variant = 'card',
  emptyChildren,
  emptyAction,
  emptySecondary,
  emptyIcon,
  emptyNarrowed,
  testId,
}: DataListProps<TNode, TFilters>) {
  const clickable = Boolean(onRowClick);
  const colCount = columns.length;
  const open = variant === 'open';
  // Column headings over nothing read as a broken table. The toolbar above
  // stays put either way, so the search and filters that produced the empty
  // result are still there to change.
  const showTable = list.loading || (!list.error && list.count > 0);
  // An empty screen and an empty result set are different situations: one is
  // waiting to be filled, the other is a search that found nothing.
  const narrowed = list.search.length > 0 || Boolean(emptyNarrowed);

  return (
    <div
      data-testid={testId}
      className={cn(
        open
          ? 'bg-transparent'
          : 'overflow-hidden rounded-lg border border-border bg-card shadow-sm',
      )}
    >
      {/* Search, applied filters and "+ Filter" read left to right as one
          sentence about what you're looking at; the order it comes back in is
          stated on the right, where it doesn't interrupt that. */}
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-2 gap-y-2',
          open ? 'px-0 pb-3' : 'p-3',
        )}
      >
        <div className="relative w-full max-w-[260px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Search"
            data-testid="list-search"
            placeholder={searchPlaceholder}
            value={list.search}
            onChange={(event) => list.setSearch(event.target.value)}
            className="h-9 pl-9"
          />
        </div>
        {filterSlot && <div className="flex flex-wrap items-center gap-2">{filterSlot}</div>}
        {(matchCount != null || sortLabel) && (
          <p className="ml-auto hidden text-xs text-muted-foreground sm:block">
            {matchCount != null
              ? `${matchCount} ${matchCount === 1 ? 'match' : 'matches'}`
              : sortLabel}
          </p>
        )}
      </div>

      {selectedFilters && <div className={cn('pb-2', open ? 'px-0' : 'px-3')}>{selectedFilters}</div>}

      {showTable && (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={cn(column.align === 'right' && 'text-right', column.className)}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.loading &&
              Array.from({ length: list.pageSize }).map((_, rowIndex) => (
                <TableRow key={`skeleton-${rowIndex}`} className="hover:bg-transparent">
                  {Array.from({ length: colCount }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <div className="h-4 w-full max-w-[8rem] animate-pulse rounded bg-muted" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}

            {!list.loading &&
              list.items.map((node) => (
                <TableRow
                  key={getRowId(node)}
                  data-testid="list-row"
                  onClick={onRowClick ? () => onRowClick(node) : undefined}
                  className={cn(clickable && 'cursor-pointer')}
                >
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      className={cn(column.align === 'right' && 'text-right', column.className)}
                    >
                      {column.cell(node)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      )}

      {!list.loading && list.error && (
        <EmptyState
          icon={TriangleAlert}
          title="This list didn't load"
          description="The connection dropped or the server is busy. Nothing has been lost."
          action={{ label: 'Try again', onAction: list.retry }}
        />
      )}

      {!list.loading && !list.error && list.count === 0 && (
        <EmptyState
          // Nothing yet is an invitation; nothing matching is a dead end you
          // back out of — the icon says which one you are looking at.
          icon={emptyIcon ?? (narrowed ? Search : Plus)}
          tone={narrowed ? 'muted' : 'primary'}
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
          secondary={emptySecondary}
        >
          {emptyChildren}
        </EmptyState>
      )}

      {!list.loading && !list.error && list.count > 0 && (
        <nav
          className={cn(
            'flex items-center justify-between border-t border-border py-3',
            open ? 'px-0' : 'px-4',
          )}
          aria-label="Pagination"
        >
          <p className="hidden text-sm text-muted-foreground sm:block">{list.rangeLabel}</p>
          <div className="flex flex-1 justify-end gap-3">
            <Button
              variant="outline"
              size="sm"
              data-testid="list-prev"
              disabled={!list.hasPrevious}
              onClick={list.prevPage}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="list-next"
              disabled={!list.hasNext}
              onClick={list.nextPage}
            >
              Next
            </Button>
          </div>
        </nav>
      )}
    </div>
  );
}
